import { Grid, IconButton, Paper, styled, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import React from "react";
import { useTranslation } from "react-i18next";

import { setMortgageAction, setNewMortgageAction } from "../../../contexts/mortgage/Actions";
import { useMortgageDispatch } from "../../../contexts/mortgage/Reducer";
import { setStepAction } from "../../../contexts/steps/Actions";
import { useStepDispatch } from "../../../contexts/steps/Reducer";
import { Mortgage } from "../../../model/MortgageExtensionModel";
import { AnalyticsActivity, AppComponent } from "../../../services/event-service/EventService";
import { useEventService } from "../../../services/event-service/EventServiceContext";
import { useFinanceCalculatorService } from "../../../services/finance-calculator/FinanceCalculatorServiceContext";
import { DateFormat, formatDate, getDifferenceInDays } from "../../../utils/DateUtils";
import { formatAmount, formatInterestRate } from "../../../utils/NumberUtils";
import { copyObject, renderProductType } from "../../../utils/Utils";

const OverviewDetailsWrapper = styled(Paper)(({ theme }) => ({
    marginBottom: "24px",
    marginTop: "8px",
    borderRadius: "4px",
    display: "flex",
    boxShadow: "0 1px 10px 0 rgba(0,0,0,0.12), 0 4px 5px 0 rgba(0,0,0,0.14), 0 2px 4px -1px rgba(0,0,0,0.2)",
}));

const DaysUntilExpiration = styled(Grid)(({ theme }) => ({
    height: "36px",
    borderTopRightRadius: "4px",
    borderTopLeftRadius: "4px",
    backgroundColor: "#FFD451",
}));

const TypographyStylingLight = styled(Typography)(({ theme }) => ({
    fontSize: "18px",
    fontWeight: 300,
    letterSpacing: "0",
    lineHeight: "26px",
    [theme.breakpoints.up("lg")]: {
        marginBottom: "24px",
    },
}));

const Amount = styled(Grid)(({ theme }) => ({
    paddingRight: "16px",
    textAlign: "right",
    paddingTop: "16px",
    [theme.breakpoints.up("lg")]: {
        paddingRight: "24px",
        paddingTop: "24px",
    },
}));

const TypographyStylingMedium = styled(Typography)(({ theme }) => ({
    fontSize: "18px",
    letterSpacing: "0",
    fontWeight: 500,
    lineHeight: "30px",
}));

const MortgageDetailsText = styled(Typography)(({ theme }) => ({
    lineHeight: "18px",
    fontSize: "14px",
    letterSpacing: "0",
    color: "rgba(0,0,0,0.54)",
    fontWeight: 400,
    marginTop: "17px",
    minWidth: "128px",
    [theme.breakpoints.up("lg")]: {
        marginTop: "24px",
    },
}));

const DurationText = styled(Typography)(({ theme }) => ({
    lineHeight: "18px",
    fontSize: "14px",
    letterSpacing: "0",
    color: "rgba(0,0,0,0.54)",
    fontWeight: 400,
    marginTop: "17px",
    minWidth: "128px",
    [theme.breakpoints.up("lg")]: {
        marginTop: "24px",
        marginLeft: "33px",
    },
}));

const DurationInfo = styled(Typography)(({ theme }) => ({
    fontSize: "18px",
    fontWeight: 300,
    letterSpacing: "0",
    lineHeight: "26px",
    [theme.breakpoints.up("lg")]: {
        marginBottom: "24px",
        marginLeft: "33px",
    },
}));

const SelectButton = styled(IconButton)(({ theme }) => ({
    borderRadius: "4px",
    backgroundColor: "#0093D1",
    height: "36px",
    width: "100%",
    marginBottom: "16px",
    alignSelf: "center",
    marginTop: "16px",
    "&:hover": {
        backgroundColor: "#0093D1",
        borderColor: "#0093D1",
        boxShadow: "none",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#0093D1",
        borderColor: "#0093D1",
    },
    [theme.breakpoints.up("lg")]: {
        marginTop: "34px",
        marginBottom: "29px",
        marginLeft: "74px",
        width: "171px",
    },
}));

const TextAboveDivider = styled(Grid)(({ theme }) => ({
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    paddingBottom: "16px",
    [theme.breakpoints.up("lg")]: {
        paddingBottom: "0px",
    },
}));

const PositionAndProductTypeText = styled(Grid)(({ theme }) => ({
    paddingTop: "16px",
    [theme.breakpoints.up("lg")]: {
        paddingTop: "24px",
        paddingLeft: "24px",
    },
    paddingLeft: "16px",
}));

const MortgageWrapper = styled(Box)(({ theme }) => ({
    width: "100%",
    flex: 1,
    display: "flex",
    flexFlow: "row nowrap",
    flexDirection: "column",
    paddingTop: "7px",
    marginLeft: "16px",
    marginRight: "16px",
    [theme.breakpoints.up("lg")]: {
        marginRight: "24px",
        marginLeft: "24px",
        paddingTop: "0px",
        flexDirection: "row",
    },
}));

const OverviewDetails = (props: { mortgage: Mortgage }) => {
    const { mortgage } = props;
    const { t, i18n } = useTranslation();
    const stepDispatch = useStepDispatch();
    const eventService = useEventService();

    const financeCalculatorService = useFinanceCalculatorService();
    financeCalculatorService.calcMonthlyPayment(mortgage);

    const mortgageDispatch = useMortgageDispatch();

    const expirationDateString = formatDate(mortgage.expirationDate, DateFormat.DATE);
    const todaysDateString = formatDate(new Date(), DateFormat.DATE);

    function processSelectedMortgage(selectedMortgage: Mortgage) {
        mortgageDispatch(setMortgageAction(selectedMortgage));
        let newMortgage: Mortgage = copyObject(selectedMortgage);
        newMortgage.duration = "0";
        newMortgage.repayment = 0;
        newMortgage.amount = selectedMortgage.amountMaturity; // DCI-1578: "in the further process steps 1 to 3 in which the extension can be made, the wrong amount (amount today) is displayed. For the extension, however, only the amount at maturity may be displayed"
        financeCalculatorService.calcTotalLiabilities(newMortgage);
        financeCalculatorService.calculateOtherParameters(newMortgage);

        eventService.sendAnalyticsEvent(AnalyticsActivity.SELECT_MORTGAGE, AppComponent.OVERVIEW_SCREEN);

        mortgageDispatch(setNewMortgageAction(newMortgage));
        stepDispatch(setStepAction(1));
    }

    function getGrid(value: string, mortgage: string) {
        return (
            <Grid data-cy="lowerGridItems" item sm={6} lg={3}>
                <MortgageDetailsText>{t(value)}</MortgageDetailsText>
                <TypographyStylingLight>{mortgage}</TypographyStylingLight>
            </Grid>
        );
    }

    return (
        <OverviewDetailsWrapper key={mortgage.id} data-cy={"mortgage-details"}>
            <Grid container>
                <TextAboveDivider container item sm={12} direction={"row"}>
                    <DaysUntilExpiration data-cy="expirationOfMortgage" item sm={12}>
                        <TypographyStylingLight sx={{ lineHeight: 2, textAlign: "center" }}>
                            {t("OverviewScreen.Your mortgage expires in days", {
                                number: getDifferenceInDays(todaysDateString, expirationDateString),
                            })}
                        </TypographyStylingLight>
                    </DaysUntilExpiration>
                    <PositionAndProductTypeText item container direction={"column"} sm={12} lg={6}>
                        <TypographyStylingMedium data-cy="productType">
                            {renderProductType(mortgage, i18n, t)}
                        </TypographyStylingMedium>
                        <TypographyStylingLight data-cy="position">
                            {t("General.Position")} {mortgage.mortgagePosition}
                        </TypographyStylingLight>
                    </PositionAndProductTypeText>
                    <Amount item sm={12} lg={6}>
                        <TypographyStylingMedium>
                            {mortgage.currency + " " + formatAmount(mortgage.amount)}
                        </TypographyStylingMedium>
                    </Amount>
                </TextAboveDivider>
                <MortgageWrapper>
                    <Grid item container>
                        {getGrid("OverviewScreen.End of term", expirationDateString)}
                        <Grid item sm={6} lg={3}>
                            <DurationText> {t("General.Years")}</DurationText>
                            <DurationInfo>{mortgage.duration}</DurationInfo>
                        </Grid>
                        {getGrid("General.Interest rate", formatInterestRate(mortgage.interestRate) + " %")}
                        <Grid data-cy="lowerGridItems" item sm={6} lg={3}>
                            <MortgageDetailsText>{t("OverviewScreen.Interest charge / month")}</MortgageDetailsText>
                            <TypographyStylingLight sx={{ minWidth: "87px" }}>
                                {mortgage.currency + " " + mortgage.monthlyPayment}
                            </TypographyStylingLight>
                        </Grid>
                    </Grid>
                    <SelectButton data-cy={"extend-mortgage-button"} onClick={() => processSelectedMortgage(mortgage)}>
                        <TypographyStylingMedium
                            sx={{
                                color: "#FFFFFF",
                                fontSize: "16px",
                                textAlign: "center",
                                lineHeight: "19px",
                            }}>
                            {t("Actions.Select mortgage")}
                        </TypographyStylingMedium>
                    </SelectButton>
                </MortgageWrapper>
            </Grid>
        </OverviewDetailsWrapper>
    );
};
export default OverviewDetails;
